import { useMemo, useState } from 'react';
import { AlertCircle, Bell, CalendarClock, CheckCircle2, Clock3, Loader2, MapPin } from 'lucide-react';
import { Button, Input } from '@tark/shared/components';
import {
  useAssignOrganizerCompetitionMatch,
  useNudgeOrganizerCompetitionMatch,
  useOrganizerCompetitionSchedule,
  useSetOrganizerCompetitionRoundDeadline,
} from '@tark/shared/hooks';
import { getErrorMessage, type CompetitionScheduleMatch } from '@tark/shared/lib';

export default function OrganizerSchedulePanel({
  competitionPublicId,
  published,
  canAdmin,
}: {
  competitionPublicId: string;
  published: boolean;
  canAdmin: boolean;
}) {
  const schedule = useOrganizerCompetitionSchedule(competitionPublicId, published);
  const assign = useAssignOrganizerCompetitionMatch(competitionPublicId);
  const setDeadline = useSetOrganizerCompetitionRoundDeadline(competitionPublicId);
  const nudge = useNudgeOrganizerCompetitionMatch(competitionPublicId);
  const [intent, setIntent] = useState<
    | { kind: 'assign'; match: CompetitionScheduleMatch }
    | { kind: 'deadline'; match: CompetitionScheduleMatch }
    | null
  >(null);
  const [dateTime, setDateTime] = useState('');
  const [courtName, setCourtName] = useState('');
  const [courtAddress, setCourtAddress] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const groups = useMemo(() => {
    const grouped = new Map<string, CompetitionScheduleMatch[]>();
    for (const match of schedule.data?.matches ?? []) {
      const key = `${match.divisionPublicId}:${match.roundNumber}`;
      grouped.set(key, [...(grouped.get(key) ?? []), match]);
    }
    return [...grouped.values()];
  }, [schedule.data?.matches]);

  if (!published) return null;

  const openIntent = (next: typeof intent) => {
    setIntent(next);
    setMessage(null);
    setDateTime('');
    setCourtName(next?.kind === 'assign' ? next.match.schedule?.courtName ?? '' : '');
    setCourtAddress(next?.kind === 'assign' ? next.match.schedule?.courtAddress ?? '' : '');
    setReason('');
  };

  const submit = async () => {
    if (!intent || !schedule.data || !dateTime || reason.trim().length < 3) return;
    setMessage(null);
    try {
      const instant = zonedDateTimeToIso(dateTime, schedule.data.timezone);
      if (intent.kind === 'assign') {
        await assign.mutateAsync({
          matchPublicId: intent.match.matchPublicId,
          request: {
            clientMutationId: createMutationId(),
            expectedMatchVersion: intent.match.version,
            startsAt: instant,
            courtName: courtName.trim() || undefined,
            courtAddress: courtAddress.trim() || undefined,
            reason: reason.trim(),
          },
        });
        setMessage({ tone: 'success', text: 'Match time updated and both sides notified.' });
      } else {
        await setDeadline.mutateAsync({
          clientMutationId: createMutationId(),
          expectedCompetitionVersion: schedule.data.competitionVersion,
          divisionPublicId: intent.match.divisionPublicId,
          roundNumber: intent.match.roundNumber,
          deadlineAt: instant,
          reason: reason.trim(),
        });
        setMessage({ tone: 'success', text: `Round ${intent.match.roundNumber} deadline updated.` });
      }
      setIntent(null);
    } catch (error) {
      setMessage({ tone: 'error', text: scheduleError(error) });
      void schedule.refetch();
    }
  };

  const sendNudge = async (match: CompetitionScheduleMatch) => {
    setMessage(null);
    try {
      await nudge.mutateAsync({ matchPublicId: match.matchPublicId, clientMutationId: createMutationId() });
      setMessage({ tone: 'success', text: 'Reminder sent.' });
    } catch (error) {
      setMessage({ tone: 'error', text: scheduleError(error) });
    }
  };

  return (
    <section className="border-b border-border py-5" aria-labelledby="competition-schedule-title">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <CalendarClock className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 id="competition-schedule-title" className="text-lg font-semibold text-text">Draw &amp; schedule</h2>
          <p className="mt-1 text-sm text-text-muted">Times are shown in {schedule.data?.timezone ?? 'the competition timezone'}.</p>
        </div>
      </div>

      {message ? (
        <p role={message.tone === 'error' ? 'alert' : 'status'} className={`mt-4 flex items-center gap-2 text-sm font-medium ${message.tone === 'error' ? 'text-red-600' : 'text-emerald-700'}`}>
          {message.tone === 'error' ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {message.text}
        </p>
      ) : null}

      {schedule.isLoading ? (
        <div className="mt-5 flex min-h-24 items-center justify-center gap-2 text-sm text-text-muted">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading schedule
        </div>
      ) : schedule.isError ? (
        <div className="mt-5 flex items-center justify-between gap-3 border-l-2 border-red-500 pl-3">
          <p className="text-sm text-text-muted">The match schedule could not be loaded.</p>
          <Button size="sm" variant="outline" onClick={() => void schedule.refetch()}>Retry</Button>
        </div>
      ) : groups.length === 0 ? (
        <p className="mt-5 text-sm text-text-muted">No published matchups are ready yet.</p>
      ) : (
        <div className="mt-5 space-y-6">
          {groups.map((matches) => {
            const first = matches[0];
            return (
              <div key={`${first.divisionPublicId}-${first.roundNumber}`}>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-text">{first.divisionName} · Round {first.roundNumber}</p>
                  {canAdmin ? (
                    <Button size="sm" variant="ghost" leftIcon={<Clock3 className="h-4 w-4" />} onClick={() => openIntent({ kind: 'deadline', match: first })}>
                      Set deadline
                    </Button>
                  ) : null}
                </div>
                <div className="divide-y divide-border border-y border-border">
                  {matches.map((match) => (
                    <div key={match.matchPublicId} className="grid gap-3 py-3 md:grid-cols-[minmax(210px,1.35fr)_minmax(170px,1fr)_auto] md:items-center">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-text">{sideNames(match.sideA.displayNames)} vs {sideNames(match.sideB.displayNames)}</p>
                        <p className="mt-1 text-xs text-text-muted">Match {match.matchNumber} · {deadlineText(match, schedule.data?.timezone)}</p>
                      </div>
                      <div className="min-w-0">
                        <Status status={match.status} />
                        {match.schedule ? (
                          <p className="mt-1 truncate text-xs text-text-muted">
                            {formatInstant(match.schedule.startsAt, schedule.data?.timezone)}
                            {match.schedule.courtName ? ` · ${match.schedule.courtName}` : ''}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2 md:justify-end">
                        <Button size="sm" variant="outline" onClick={() => openIntent({ kind: 'assign', match })}>
                          {match.schedule ? 'Reschedule' : 'Assign'}
                        </Button>
                        <Button size="sm" variant="ghost" aria-label="Send scheduling reminder" title="Send scheduling reminder" onClick={() => void sendNudge(match)}>
                          <Bell className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {intent ? (
        <div className="mt-5 border-l-2 border-primary pl-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-text">{intent.kind === 'assign' ? 'Assign match time' : `Round ${intent.match.roundNumber} deadline`}</p>
              <p className="mt-1 text-xs text-text-muted">{sideNames(intent.match.sideA.displayNames)} vs {sideNames(intent.match.sideB.displayNames)}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setIntent(null)}>Close</Button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <Input label={intent.kind === 'assign' ? 'Match time' : 'Deadline'} type="datetime-local" value={dateTime} onChange={(event) => setDateTime(event.target.value)} />
            {intent.kind === 'assign' ? <Input label="Court" value={courtName} onChange={(event) => setCourtName(event.target.value)} leftIcon={<MapPin className="h-4 w-4" />} /> : null}
            {intent.kind === 'assign' ? <Input label="Address (optional)" value={courtAddress} onChange={(event) => setCourtAddress(event.target.value)} /> : null}
            <Input label="Reason" value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} />
          </div>
          <div className="mt-4 flex justify-end">
            <Button disabled={!dateTime || reason.trim().length < 3 || assign.isPending || setDeadline.isPending} onClick={() => void submit()}>
              {assign.isPending || setDeadline.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function Status({ status }: { status: string }) {
  const styles: Record<string, string> = {
    SCHEDULED: 'bg-emerald-500/10 text-emerald-700',
    SCHEDULED_PAST_DEADLINE: 'bg-amber-500/10 text-amber-700',
    PROPOSED: 'bg-amber-500/10 text-amber-700',
    OVERDUE: 'bg-red-500/10 text-red-700',
    ACTION_REQUIRED: 'bg-red-500/10 text-red-700',
    NO_SCHEDULE: 'bg-surface-elevated text-text-muted',
  };
  const labels: Record<string, string> = {
    SCHEDULED: 'Scheduled', SCHEDULED_PAST_DEADLINE: 'Scheduled after deadline',
    PROPOSED: 'Proposed', OVERDUE: 'Overdue',
    ACTION_REQUIRED: 'Action required', NO_SCHEDULE: 'Not scheduled',
  };
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${styles[status] ?? styles.NO_SCHEDULE}`}>{labels[status] ?? status}</span>;
}

function sideNames(names: string[]): string {
  return names.filter(Boolean).join(' / ') || 'Player unavailable';
}

function deadlineText(match: CompetitionScheduleMatch, timeZone?: string): string {
  return match.deadlineAt ? `Due ${formatInstant(match.deadlineAt, timeZone)}` : 'No deadline';
}

function formatInstant(value: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(value));
}

function zonedDateTimeToIso(value: string, timeZone: string): string {
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  const wanted = Date.UTC(year, month - 1, day, hour, minute);
  let guess = wanted;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(guess)).map((part) => [part.type, part.value]));
    const rendered = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
    guess += wanted - rendered;
  }
  return new Date(guess).toISOString();
}

function createMutationId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function scheduleError(error: unknown): string {
  const message = getErrorMessage(error).toLowerCase();
  if (message.includes('version') || message.includes('changed')) return 'The match changed. Refresh and try again.';
  if (message.includes('reminder') || message.includes('nudge')) return 'A reminder was already sent today.';
  if (message.includes('past')) return 'Choose a future time in the competition timezone.';
  return 'The schedule could not be updated. Refresh and try again.';
}
